// src/pages/superadmin/SuperAdminDashboard.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .dash-brand-logo { width: 36px; height: 36px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; }
  .dash-brand-logo img { width: 100%; height: 100%; object-fit: contain; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN */
  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

  /* STATS */
  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px 18px; box-shadow: var(--shadow-sm); position: relative; overflow: hidden; transition: box-shadow var(--t), border-color var(--t), transform var(--t); }
  .stat-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--accent); opacity: 0; transition: opacity var(--t); }
  .stat-card:hover { box-shadow: var(--shadow-md); border-color: var(--border-strong); transform: translateY(-1px); }
  .stat-card:hover::before { opacity: 1; }
  .stat-label { font-family: var(--mono); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); margin-bottom: 10px; }
  .stat-value { font-size: 30px; font-weight: 600; letter-spacing: -0.03em; line-height: 1; color: var(--text); }
  .stat-value.green { color: var(--green); }
  .stat-value.amber { color: var(--amber); }
  .stat-value.accent { color: var(--accent); }

  /* ACTION CARDS */
  .actions-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .action-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; display: flex; align-items: center; gap: 16px; text-decoration: none; transition: box-shadow var(--t), border-color var(--t), transform var(--t); box-shadow: var(--shadow-sm); cursor: pointer; }
  .action-card:hover { border-color: var(--border-strong); box-shadow: var(--shadow-md); transform: translateY(-1px); }
  .action-icon { width: 42px; height: 42px; border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .action-icon.blue { background: var(--accent-dim); border: 1px solid rgba(8,42,88,0.15); }
  .action-icon.green { background: var(--green-dim); border: 1px solid var(--green-border); }
  .action-icon svg { width: 20px; height: 20px; }
  .action-icon.blue svg { color: var(--accent); }
  .action-icon.green svg { color: var(--green); }
  .action-title { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; }
  .action-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); margin-top: 2px; }

  /* PANEL / TABLE */
  .panel { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .panel-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; border-bottom: 1px solid var(--border); background: var(--surface-sub); gap: 12px; }
  .panel-title { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; }
  .table-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { border-bottom: 1px solid var(--border); background: var(--surface-sub); }
  th { padding: 9px 16px; text-align: left; font-family: var(--mono); font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); font-weight: 500; white-space: nowrap; }
  tbody tr { border-bottom: 1px solid var(--border); background: var(--surface); transition: background var(--t); }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--surface-sub); }
  td { padding: 13px 16px; vertical-align: middle; font-size: 13px; }
  .cell-primary { font-size: 13px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; }
  .cell-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); margin-top: 2px; }
  .cell-meta { font-family: var(--mono); font-size: 12px; color: var(--text-muted); }

  /* BADGE */
  .badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 9px; border-radius: 100px; font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; }
  .badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .badge-active { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .badge-active .badge-dot { background: var(--green); }
  .badge-inactive { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .badge-inactive .badge-dot { background: var(--red); }

  /* LINK BUTTON */
  .btn-link { background: none; border: none; padding: 0; font-family: var(--mono); font-size: 11.5px; color: var(--accent); cursor: pointer; text-decoration: none; font-weight: 500; letter-spacing: 0.02em; transition: opacity var(--t); }
  .btn-link:hover { opacity: 0.65; }

  /* LOADING */
  .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 24px; gap: 14px; }
  .spinner { width: 32px; height: 32px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); }
`;

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    totalUsers: 0,
    unassignedCompanies: 0,
  });
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);
  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadDashboardData = async () => {
    try {
      const [customersRes, companiesRes] = await Promise.all([
        apiService.getCustomers(),
        apiService.getUnassignedCompanies({ limit: 1 }),
      ]);
      const customers = customersRes.data;
      const activeCustomers = customers.filter(
        (c) => c.licenseStatus === "active",
      );
      const totalUsers = customers.reduce(
        (sum, c) => sum + (c.users?.length || 0),
        0,
      );
      setStats({
        totalCustomers: customers.length,
        activeCustomers: activeCustomers.length,
        totalUsers,
        unassignedCompanies: companiesRes.pagination.total,
      });
      setRecentCustomers(customers.slice(0, 5));
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`dash-root${darkMode ? " dark" : ""}`}>
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <div className="dash-brand-logo">
                <img src="/assets/logo.png" alt="Axo Signal" />
              </div>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Super Admin</div>
                <div className="dash-brand-sub">Axo Signal</div>
              </div>
            </div>
            <div className="dash-header-right">
              <NotificationBell />
              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn-header"
                  onClick={() => setSettingsOpen((o) => !o)}
                >
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
                <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                    />
                  </div>
                </div>
              </div>
              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn-header">
                Logout
              </button>
            </div>
          </div>
        </header>

        <div className="dash-main">
          {/* Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Total Customers</div>
              <div className="stat-value">{stats.totalCustomers}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active Licenses</div>
              <div className="stat-value green">{stats.activeCustomers}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Users</div>
              <div className="stat-value accent">{stats.totalUsers}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Unassigned Leads</div>
              <div className="stat-value amber">
                {stats.unassignedCompanies}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="actions-grid">
            <Link to="/super-admin/customers" className="action-card">
              <div className="action-icon blue">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Manage Customers</div>
                <div className="action-sub">Create and configure accounts</div>
              </div>
            </Link>
            <Link to="/super-admin/onboarding" className="action-card">
              <div className="action-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l2 2 4-4"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Onboarding Queue</div>
                <div className="action-sub">
                  Map services and start billing
                </div>
              </div>
            </Link>
            <Link to="/super-admin/companies" className="action-card">
              <div className="action-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Company Pool</div>
                <div className="action-sub">Assign companies to customers</div>
              </div>
            </Link>
            <Link to="/super-admin/exclusions" className="action-card">
              <div className="action-icon amber">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Company Exclusions</div>
                <div className="action-sub">
                  Manual list of companies that cannot buy
                </div>
              </div>
            </Link>
            <Link to="/super-admin/qa" className="action-card">
              <div className="action-icon blue">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">QA Queue</div>
                <div className="action-sub">
                  Review generated sites before go-live
                </div>
              </div>
            </Link>
            <Link to="/super-admin/form-submissions" className="action-card">
              <div className="action-icon blue">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Brief Submissions</div>
                <div className="action-sub">
                  Client brief-form answers, uploads &amp; Duda build status
                </div>
              </div>
            </Link>
            <Link to="/super-admin/partner-outreach" className="action-card">
              <div className="action-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">Partner Outreach</div>
                <div className="action-sub">
                  6ELA &amp; Wizzyl partner-recruitment templates
                </div>
              </div>
            </Link>
            <Link to="/super-admin/ai-governance" className="action-card">
              <div className="action-icon blue">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.751.43.991l1.004.828c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a7.72 7.72 0 010-.255c.007-.378-.138-.751-.43-.991l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="action-title">AI Governance</div>
                <div className="action-sub">
                  Provider/model, cost caps &amp; enable-disable per AI task
                  type
                </div>
              </div>
            </Link>
          </div>

          {/* Recent Customers */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Recent Customers</div>
              <Link to="/super-admin/customers" className="btn-link">
                View all →
              </Link>
            </div>
            {loading ? (
              <div className="loading-state" style={{ width: "100%" }}>
                <Skeleton lines={5} label="Loading recent customers" />
              </div>
            ) : (
              <SortableDataTable
                caption="Recent customers"
                rowKey="id"
                defaultSort={{ key: "createdAt", direction: "desc" }}
                rows={recentCustomers.map((customer) => ({
                  ...customer,
                  _users: customer.users?.length || 0,
                }))}
                columns={[
                  {
                    key: "name",
                    label: "Customer",
                    render: (value, row) => (
                      <>
                        <div className="cell-primary">{value}</div>
                        <div className="cell-sub">{row.slug}</div>
                      </>
                    ),
                  },
                  {
                    key: "licenseStatus",
                    label: "License",
                    sortable: false,
                    render: (value) => (
                      <span
                        className={`badge ${value === "active" ? "badge-active" : "badge-inactive"}`}
                      >
                        <span className="badge-dot" />
                        {value}
                      </span>
                    ),
                  },
                  {
                    key: "_users",
                    label: "Users",
                    numeric: true,
                    render: (value) => <div className="cell-meta">{value}</div>,
                  },
                  {
                    key: "createdAt",
                    label: "Created",
                    render: (value) => (
                      <div className="cell-meta">
                        {new Date(value).toLocaleDateString("en-GB")}
                      </div>
                    ),
                  },
                  {
                    key: "_actions",
                    label: "",
                    sortable: false,
                    render: () => (
                      <Link to="/super-admin/customers" className="btn-link">
                        Manage →
                      </Link>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard;
